import {
  getChromeConfiguration,
  PageHeader,
  PageLayoutType,
  setChromeConfiguration,
  ItemList,
  KeyValue,
  ObjectInfo,
  getCommonEventTarget,
  MarketplaceSwitcher
} from '@amzn/aac-chrome-api';
import { StandardChromeConfigurationProps, FocusChromeConfigurationProps, ChromeConfigurationPropsBase } from './types';
import { getAgentId } from '@flexible-reports/utils/analysis-utils';
import { isCaracalA3CIntegrationEnabled, isAnalysisWorkspaceEnabled } from '@flexible-reports/utils/app-utils';

const EXIT_FOCUS_VIEW = 'ExitFocusView';
// TODO: use types from the 'aac-chrome-api' https://sim.amazon.com/issues/RCMRX-10965
const PAGE_SCOPE_ALL_ACCOUNTS = 'ALL_ACCOUNTS';

const setURCChromeConfiguration = (props: ChromeConfigurationPropsBase, layoutType: string) => {
  const config = getChromeConfiguration();

  const marketplaceSwitcher = new MarketplaceSwitcher();
  marketplaceSwitcher.setUseMarketplaceSwitcher(props.enableMarketplaceSwitcher);
  config.setMarketplaceSwitcher(marketplaceSwitcher);

  config.setPivotNav(props.pivotMenu);

  const pageHeader = new PageHeader();

  if (props.useStandardPageHeader) {
    config.setPageLayoutType(PageLayoutType.STANDARD);
  } else {
    const objectInfo = new ObjectInfo();
    objectInfo.setObjectType(props.objectType);

    const actions = new ItemList();

    if (props.buttons && props.buttons.length > 0) {
      props.buttons.forEach(button => actions.addItem(button));
    }

    if (props.actionDropdown) {
      actions.addItem(props.actionDropdown);
    }

    if (actions.size() > 0) {
      objectInfo.setActions(actions);
    }

    const attributes = new ItemList();
    if (props.toggle) {
      attributes.addItem(props.toggle);
    }

    if (props.keyValuePairs) {
      props.keyValuePairs.forEach(keyValuePair => {
        const keyValue = new KeyValue();
        keyValue.setId(keyValuePair.id);
        keyValue.setKey(keyValuePair.key);
        keyValue.setValue(keyValuePair.value);
        attributes.addItem(keyValue);
      });
    }

    objectInfo.setAttributes(attributes);

    if (props.attributeErrorMessage) {
      objectInfo.setAttributeErrorMessage(props.attributeErrorMessage);
    }
    pageHeader.setObjectInfo(objectInfo);
    config.setPageLayoutType(layoutType);
  }

  pageHeader.setPageName(props.pageName);
  pageHeader.setPageScope(PAGE_SCOPE_ALL_ACCOUNTS);
  config.setPageHeader(pageHeader);
  if (props.breadcrumbItems) {
    config.setBreadcrumbs(props.breadcrumbItems);
  } else {
    config.setBreadcrumbs([]);
  }

  if (props.pageTitleBreadcrumb) {
    config.setPageTitleBreadcrumb(props.pageTitleBreadcrumb);
  } else {
    config.setPageTitleBreadcrumb();
  }

  if (isCaracalA3CIntegrationEnabled() || isAnalysisWorkspaceEnabled()) {
    config.setEditableEmbeddedChatConfig({
      panelSizes: {
        defaultWidth: 600,
        minWidth: 475
      },
      selectedAgentIdentifier: getAgentId()
    });
  }

  setChromeConfiguration(config);
};

/**
 * This is a helper function to configure the standard the chrome nav. You should be calling this function inside of a
 * useEffect hook. This helper cannot do the useEffect wrapper for you because it is difficult to optimally configure
 * the useEffect hook to ensure that it does not run on every rerender.
 * Example usage:
 *   useEffect(() => {
 *     setStandardLayoutChromeConfiguration({ buttons: [item1, item2 ]})
 *   }, [item1, item2])
 * @param props chrome elements to pass to chrome nav
 */
const setStandardLayoutChromeConfiguration = (props: StandardChromeConfigurationProps) => {
  setURCChromeConfiguration(props, PageLayoutType.STANDARD);
};

/**
 * This is a helper function to configure the focus mode chrome nav. You should be calling this function inside of a
 * useEffect hook. This helper cannot do the useEffect wrapper for you because it is difficult to optimally configure
 * the useEffect hook to ensure that it does not run on every rerender.
 * Example usage:
 *   useEffect(() => {
 *     return setFocusLayoutChromeConfiguration({ buttons: [item1, item2 ]})
 *   }, [item1, item2])
 * @param props chrome elements to pass to chrome nav
 * @returns event listener clean up. Ensure that you are returning this to useEffect
 */
const setFocusLayoutChromeConfiguration = (props: FocusChromeConfigurationProps) => {
  setURCChromeConfiguration(props, PageLayoutType.FOCUS);
  const eventTarget = getCommonEventTarget();
  eventTarget.addEventListener(EXIT_FOCUS_VIEW, props.onExit);
  return () => {
    eventTarget.removeEventListener(EXIT_FOCUS_VIEW, props.onExit);
  };
};

const setStandardLayoutAllAccountChromeConfiguration = (props: StandardChromeConfigurationProps) => {
  setURCChromeConfiguration(props, PageLayoutType.STANDARD);
};

/**
 * This is a helper function to configure the focus mode chrome nav. You should be calling this function inside of a
 * useEffect hook. This helper cannot do the useEffect wrapper for you because it is difficult to optimally configure
 * the useEffect hook to ensure that it does not run on every rerender.
 * Example usage:
 *   useEffect(() => {
 *     return setFocusLayoutChromeConfiguration({ buttons: [item1, item2 ]})
 *   }, [item1, item2])
 * @param props chrome elements to pass to chrome nav
 * @returns event listener clean up. Ensure that you are returning this to useEffect
 */
const setFocusLayoutAllAccountChromeConfiguration = (props: FocusChromeConfigurationProps) => {
  setURCChromeConfiguration(props, PageLayoutType.FOCUS);
  const eventTarget = getCommonEventTarget();
  eventTarget.addEventListener(EXIT_FOCUS_VIEW, props.onExit);
  return () => {
    eventTarget.removeEventListener(EXIT_FOCUS_VIEW, props.onExit);
  };
};

export {
  setStandardLayoutChromeConfiguration,
  setFocusLayoutChromeConfiguration,
  setStandardLayoutAllAccountChromeConfiguration,
  setFocusLayoutAllAccountChromeConfiguration
};
